import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Grid,
  Tab,
  Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useWarbandStore } from '../../stores/warbandStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { Weapon, Armour, SpecialTag } from '../../types';
import { CustomContentManager } from '../shared/CustomContentManager';
import { SelectSpecialTagsModal } from './SelectSpecialTagsModal';

type TabIndex = 0 | 1;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseNum(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return isNaN(n) ? null : n;
}

// ---------------------------------------------------------------------------
// Weapon form
// ---------------------------------------------------------------------------

interface WeaponForm {
  name: string;
  damageModifier: string;
  maxRange: string;
  encumbering: boolean;
  slotsConsumed: string;
  selectedTags: SpecialTag[];
}

const emptyWeaponForm = (): WeaponForm => ({
  name: '',
  damageModifier: '0',
  maxRange: '',
  encumbering: true,
  slotsConsumed: '1',
  selectedTags: [],
});

function weaponToForm(w: Weapon): WeaponForm {
  return {
    name: w.name,
    damageModifier: w.damageModifier !== null ? String(w.damageModifier) : '0',
    maxRange: w.maxRange !== null ? String(w.maxRange) : '',
    encumbering: w.encumbering === 1,
    slotsConsumed: w.slotsConsumed !== null ? String(w.slotsConsumed) : '1',
    selectedTags: w.specialTags,
  };
}

// ---------------------------------------------------------------------------
// Armour form
// ---------------------------------------------------------------------------

interface ArmourForm {
  name: string;
  armourModifier: string;
  encumbering: boolean;
  selectedTags: SpecialTag[];
}

const emptyArmourForm = (): ArmourForm => ({
  name: '',
  armourModifier: '0',
  encumbering: true,
  selectedTags: [],
});

function armourToForm(a: Armour): ArmourForm {
  return {
    name: a.name,
    armourModifier: a.armourModifier !== null ? String(a.armourModifier) : '0',
    encumbering: a.encumbering === 1,
    selectedTags: a.specialTags,
  };
}

// ---------------------------------------------------------------------------
// Shared special-tags picker section
// ---------------------------------------------------------------------------

const SpecialTagsField: React.FC<{
  selectedTags: SpecialTag[];
  onChange: (tags: SpecialTag[]) => void;
}> = ({ selectedTags, onChange }) => {
  const [tagModalOpen, setTagModalOpen] = useState(false);
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setTagModalOpen(true)}>
          Add Special Tag
        </Button>
      </Box>
      {selectedTags.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
          {selectedTags.map((tag) => (
            <Chip key={tag.id} label={tag.id} size="small" onDelete={() => onChange(selectedTags.filter((t) => t.id !== tag.id))} />
          ))}
        </Stack>
      )}
      <SelectSpecialTagsModal
        open={tagModalOpen}
        excludeTagIds={selectedTags.map((t) => t.id)}
        onClose={() => setTagModalOpen(false)}
        onSelect={(tag) => {
          onChange([...selectedTags, tag]);
          setTagModalOpen(false);
        }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Weapons tab
// ---------------------------------------------------------------------------

const WeaponFormFields: React.FC<{
  form: WeaponForm;
  setForm: React.Dispatch<React.SetStateAction<WeaponForm>>;
}> = ({ form, setForm }) => {
  const setField = <K extends keyof WeaponForm>(k: K, v: WeaponForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextField label="Name" value={form.name} onChange={(e) => setField('name', e.target.value)} fullWidth required size="small" />
      </Grid>
      <Grid size={{ xs: 6, sm: 4 }}>
        <TextField label="Damage Modifier" type="number" value={form.damageModifier} onChange={(e) => setField('damageModifier', e.target.value)} fullWidth size="small" />
      </Grid>
      <Grid size={{ xs: 6, sm: 4 }}>
        <TextField label="Max Range" type="number" value={form.maxRange} onChange={(e) => setField('maxRange', e.target.value)} fullWidth size="small" placeholder="None" />
      </Grid>
      <Grid size={{ xs: 6, sm: 4 }}>
        <TextField label="Slots Consumed" type="number" value={form.slotsConsumed} onChange={(e) => setField('slotsConsumed', e.target.value)} fullWidth size="small" />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormControlLabel
          control={<Switch checked={form.encumbering} onChange={(e) => setField('encumbering', e.target.checked)} />}
          label="Encumbering"
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <SpecialTagsField selectedTags={form.selectedTags} onChange={(tags) => setField('selectedTags', tags)} />
      </Grid>
    </Grid>
  );
};

function WeaponsTab() {
  const { customWeapons, addCustomWeapon, updateCustomWeapon, deleteCustomWeapon } =
    useReferenceDataStore();

  return (
    <CustomContentManager<Weapon, WeaponForm>
      itemLabel="Weapon"
      items={customWeapons}
      getItemName={(w) => w.name}
      getItemSecondary={(w) => [
        w.damageModifier !== null ? `DMG ${w.damageModifier > 0 ? '+' : ''}${w.damageModifier}` : null,
        w.maxRange !== null ? `Range ${w.maxRange}"` : null,
        w.specialTags.length > 0 ? w.specialTags.map((t) => t.id).join(', ') : null,
      ].filter(Boolean).join(' · ') || undefined}
      emptyForm={emptyWeaponForm}
      itemToForm={weaponToForm}
      validate={(form) => (form.name.trim() ? null : 'Name is required.')}
      renderFormFields={(form, setForm) => <WeaponFormFields form={form} setForm={setForm} />}
      onSave={(form, editingId) => {
        const data: Omit<Weapon, 'id' | 'isCustom'> = {
          name: form.name.trim(),
          damageModifier: parseNum(form.damageModifier) ?? 0,
          maxRange: parseNum(form.maxRange),
          encumbering: form.encumbering ? 1 : null,
          slotsConsumed: parseNum(form.slotsConsumed) ?? 1,
          notes: null,
          description: null,
          specialTags: form.selectedTags,
        };
        if (editingId) updateCustomWeapon(editingId, data);
        else addCustomWeapon(data);
      }}
      onDelete={deleteCustomWeapon}
    />
  );
}

// ---------------------------------------------------------------------------
// Armour tab
// ---------------------------------------------------------------------------

const ArmourFormFields: React.FC<{
  form: ArmourForm;
  setForm: React.Dispatch<React.SetStateAction<ArmourForm>>;
}> = ({ form, setForm }) => {
  const setField = <K extends keyof ArmourForm>(k: K, v: ArmourForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextField label="Name" value={form.name} onChange={(e) => setField('name', e.target.value)} fullWidth required size="small" />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <TextField label="Armour Modifier" type="number" value={form.armourModifier} onChange={(e) => setField('armourModifier', e.target.value)} fullWidth size="small" />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormControlLabel
          control={<Switch checked={form.encumbering} onChange={(e) => setField('encumbering', e.target.checked)} />}
          label="Encumbering"
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <SpecialTagsField selectedTags={form.selectedTags} onChange={(tags) => setField('selectedTags', tags)} />
      </Grid>
    </Grid>
  );
};

function ArmourTab() {
  const { customArmours, addCustomArmour, updateCustomArmour, deleteCustomArmour } =
    useReferenceDataStore();

  return (
    <CustomContentManager<Armour, ArmourForm>
      itemLabel="Armour"
      emptyMessage="No custom armour yet."
      items={customArmours}
      getItemName={(a) => a.name}
      getItemSecondary={(a) => [
        a.armourModifier !== null ? `Armour ${a.armourModifier > 0 ? '+' : ''}${a.armourModifier}` : null,
        a.specialTags.length > 0 ? a.specialTags.map((t) => t.id).join(', ') : null,
      ].filter(Boolean).join(' · ') || undefined}
      emptyForm={emptyArmourForm}
      itemToForm={armourToForm}
      validate={(form) => (form.name.trim() ? null : 'Name is required.')}
      renderFormFields={(form, setForm) => <ArmourFormFields form={form} setForm={setForm} />}
      onSave={(form, editingId) => {
        const data: Omit<Armour, 'id' | 'isCustom'> = {
          name: form.name.trim(),
          armourModifier: parseNum(form.armourModifier) ?? 0,
          encumbering: form.encumbering ? 1 : null,
          notes: null,
          description: null,
          specialTags: form.selectedTags,
        };
        if (editingId) updateCustomArmour(editingId, data);
        else addCustomArmour(data);
      }}
      onDelete={deleteCustomArmour}
    />
  );
}

// ---------------------------------------------------------------------------
// Modal wrapper
// ---------------------------------------------------------------------------

export function ManageCustomEquipmentModal() {
  const { ui, closeCustomEquipmentModal } = useWarbandStore();
  const [tabIndex, setTabIndex] = useState<TabIndex>(0);

  return (
    <Dialog
      open={ui.customEquipmentModalOpen}
      onClose={closeCustomEquipmentModal}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Custom Equipment</DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_e, v) => setTabIndex(v as TabIndex)}
          sx={{ mb: 2 }}
        >
          <Tab label="Weapons" />
          <Tab label="Armour" />
        </Tabs>
        {tabIndex === 0 && ui.customEquipmentModalOpen && <WeaponsTab />}
        {tabIndex === 1 && ui.customEquipmentModalOpen && <ArmourTab />}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCustomEquipmentModal}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
